import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    Star16Regular,
    Star16Filled,
    Comment16Filled,
    CommentAdd16Regular,
    Checkmark16Regular,
    ErrorCircle16Regular,
    Filter20Regular,
    Delete16Regular,
} from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover, MenuList } from '@fluentui/react-components';
import { PatchTask } from '../../redux/taskSlice';
import styles from './css/index.module.css';
import { taskTableMeasuring } from './taskDndConfig';
import { CustomAvatar } from '.';
import { getVisibleTaskTags } from '../../utils/taskSchedulingUtils';

const TASK_KANBAN_STATUSES_STANDARD = [
    { value: 'NOT_STARTED', label: 'Not Started', color: '#C0C6D8' },
    { value: 'WORKING_ON_IT', label: 'Working on it', color: 'var(--RAG-Amber)' },
    { value: 'BLOCKED', label: 'Blocked', color: 'var(--RAG-Red)' },
    { value: 'PAUSED', label: 'Paused', color: '#D5D8E0' },
    { value: 'COMPLETED', label: 'Completed', color: 'var(--RAG-Green)' },
    { value: 'CANCELLED', label: 'Cancelled', color: '#989BAD' },
];

const TASK_KANBAN_STATUSES_SCHEDULING = [
    { value: 'NOT_STARTED', label: 'Not Started', color: '#C0C6D8' },
    { value: 'WORKING_ON_IT', label: 'Working on it', color: 'var(--RAG-Amber)' },
    { value: 'BLOCKED', label: 'Blocked', color: 'var(--RAG-Red)' },
    { value: 'PAUSED', label: 'Paused', color: '#D5D8E0' },
    { value: 'COMPLETED', label: 'Completed', color: 'var(--RAG-Green)' },
    { value: 'CANCELLED', label: 'Cancelled', color: '#989BAD' },
];

const TaskKanbanBoard = ({ tasks, onStatusChange, projectUsersAll, onTaskClick, onTaskEdit, onTaskDelete, onOpenSidebar, previewTaskId = null }) => {
    const dispatch = useDispatch();
    const taskData = useSelector((state) => state.task?.taskData) || [];
    const selectedProject = useSelector(state => state.projectData?.selectedProject);
    const TASK_KANBAN_STATUSES = TASK_KANBAN_STATUSES_SCHEDULING;

    const normalizeStatus = status => {
        if (!status) return 'NOT_STARTED';
        const normalized = String(status).trim();
        if (!normalized) return 'NOT_STARTED';
        if (normalized === 'NOT_APPLICABLE' || normalized === 'UNDEFINED') return 'NOT_STARTED';
        if (normalized.toLowerCase() === 'not applicable' || normalized.toLowerCase() === 'undefined') return 'NOT_STARTED';
        if (normalized === 'STUCK') return 'BLOCKED';
        return normalized;
    };

    // Filter state
    const [filters, setFilters] = React.useState([{ id: 1, column: '', value: '' }]);

    // Ensure tasks is always an array
    const safeTasks = React.useMemo(() => (Array.isArray(tasks) ? tasks : []), [tasks]);

    // Pre-calculate which tasks have sub-tasks (optimization)
    const tasksWithSubTasks = React.useMemo(() => {
        const hasSubTasksSet = new Set();
        safeTasks.forEach(task => {
            if (task.parent_task) {
                hasSubTasksSet.add(task.parent_task);
            }
        });
        return hasSubTasksSet;
    }, [safeTasks]);

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 5,
            },
        }),
    );

    // Handle star click - same logic as TasksTable
    const handleStarClick = async (e, task) => {
        e.stopPropagation();

        if (!task.starred) {
            // Only one key task per project — unstar any other starred tasks first
            const otherStarred = (taskData || []).filter(t => t.id !== task.id && t.starred);
            for (const other of otherStarred) {
                await dispatch(PatchTask({ body: { starred: false }, id: other.id, suppressToast: true }));
            }
            // Star this task
            await dispatch(PatchTask({ body: { starred: true }, id: task.id, suppressToast: true }));
        } else {
            // Unstar this task
            await dispatch(PatchTask({ body: { starred: false }, id: task.id, suppressToast: true }));
        }
    };

    // Active drag state
    const [activeId, setActiveId] = React.useState(null);
    const containerRef = React.useRef(null);

    // Handle drag start
    const handleDragStart = event => {
        setActiveId(event.active.id);
    };

    // Handle drag end
    const handleDragEnd = event => {
        const { active, over } = event;
        setActiveId(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Find the task being moved
        const task = safeTasks.find(t => t.id.toString() === activeId);
        if (!task) {
            console.warn('Task not found for activeId:', activeId);
            return;
        }

        // Determine the new status
        let newStatus;

        // Check if we're dropping on a status column (droppable area)
        const validStatuses = TASK_KANBAN_STATUSES.map(s => s.value);
        if (validStatuses.includes(overId)) {
            // Dropping on a status column
            newStatus = overId;
        } else {
            // Dropping on another task - find which status column it belongs to
            const overTask = safeTasks.find(t => t.id.toString() === overId);
            if (overTask) {
                newStatus = normalizeStatus(overTask.status);
            } else {
                // Fallback - don't change status
                return;
            }
        }

        // Call the status change handler if status actually changed
        if (onStatusChange && normalizeStatus(task.status) !== newStatus) {
            onStatusChange(task, newStatus);
        }
    };

    // Get the active task for drag overlay
    const activeTask = React.useMemo(() => {
        return safeTasks.find(task => task.id.toString() === activeId);
    }, [activeId, safeTasks]);

    // Get column options for filtering
    const getColumnOptions = () => [
        { value: 'status', label: 'Status' },
        { value: 'priority', label: 'Priority' },
        { value: 'workstream', label: 'Workstream' },
        { value: 'tag', label: 'Tag' },
        { value: 'assigned_to', label: 'Assigned To' },
    ];

    // Get value options based on selected column
    const getValueOptions = column => {
        if (!column) return [];

        const uniqueValues = [
            ...new Set(
                safeTasks.map(task => {
                    switch (column) {
                        case 'status':
                            return normalizeStatus(task.status);
                        case 'priority':
                            return task.priority || 'MEDIUM';
                        case 'workstream':
                            return task.workstream_repr || 'Unknown';
                        case 'tag':
                            return task.tag_repr || 'Unknown';
                        case 'assigned_to':
                            return task.assigned_to_name || 'Unassigned';
                        default:
                            return '';
                    }
                }),
            ),
        ];

        return uniqueValues.map(value => {
            let label = value;

            // Format labels for better display
            switch (column) {
                case 'status':
                    // Convert status values to readable format
                    const statusMap = {
                        NOT_STARTED: 'Not Started',
                        WORKING_ON_IT: 'Working on it',
                        BLOCKED: 'Blocked',
                        STUCK: 'Blocked',
                        PAUSED: 'Paused',
                        COMPLETED: 'Completed',
                        CANCELLED: 'Cancelled',
                        NOT_APPLICABLE: 'Not Started',
                    };
                    label = statusMap[value] || value;
                    break;
                case 'priority':
                    // Convert priority values to readable format
                    const priorityMap = {
                        HIGH: 'High',
                        MEDIUM: 'Medium',
                        LOW: 'Low',
                    };
                    label = priorityMap[value] || value;
                    break;
                case 'workstream':
                case 'tag':
                case 'assigned_to':
                    // These are already in readable format
                    label = value;
                    break;
                default:
                    label = value;
            }

            return { value, label };
        });
    };

    const filteredTasks = React.useMemo(() => {
        if (!safeTasks) return [];

        return safeTasks.filter(task => {
            if (task.parent_task) {
                return false;
            }

            return filters.every(filter => {
                if (!filter.column || !filter.value) return true;

                let taskValue = '';
                switch (filter.column) {
                    case 'status':
                        taskValue = normalizeStatus(task.status);
                        break;
                    case 'priority':
                        taskValue = task.priority || 'MEDIUM';
                        break;
                    case 'workstream':
                        taskValue = task.workstream_repr || 'Unknown';
                        break;
                    case 'tag':
                        taskValue = task.tag_repr || 'Unknown';
                        break;
                    case 'assigned_to':
                        taskValue = task.assigned_to_name || 'Unassigned';
                        break;
                    default:
                        return true;
                }

                return taskValue === filter.value;
            });
        });
    }, [safeTasks, filters]);

    const tasksByStatus = React.useMemo(() => {
        const grouped = {};
        TASK_KANBAN_STATUSES.forEach(status => {
            grouped[status.value] = [];
        });
        filteredTasks.forEach(task => {
            const statusKey = normalizeStatus(task.status);
            if (grouped[statusKey]) {
                grouped[statusKey].push(task);
            }
        });
        return grouped;
    }, [filteredTasks, TASK_KANBAN_STATUSES]);

    // Filter management functions
    const handleAddFilter = () => {
        const newId = Math.max(...filters.map(f => f.id), 0) + 1;
        setFilters([...filters, { id: newId, column: '', value: '' }]);
    };

    const handleUpdateFilter = (id, field, value) => {
        setFilters(filters.map(filter => (filter.id === id ? { ...filter, [field]: value, ...(field === 'column' ? { value: '' } : {}) } : filter)));
    };

    const handleRemoveFilter = id => {
        if (filters.length > 1) {
            setFilters(filters.filter(filter => filter.id !== id));
        }
    };

    const handleClearAllFilters = () => {
        setFilters([{ id: 1, column: '', value: '' }]);
    };

    return (
        <div>
            <div className={styles.filterHeaderKanban}>
                <Menu>
                    <MenuTrigger>
                        <span className={styles.linkText} style={{ cursor: 'pointer' }}>
                            <Filter20Regular className="me-1" />
                            Filter ({filters.filter(f => f.column && f.value).length})
                        </span>
                    </MenuTrigger>
                    <MenuPopover style={{ width: '280px', minWidth: '280px' }}>
                        <MenuList>
                            <div className="p-2">
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <h6 className={styles.tableFilterHeader + ' mb-0'}>Filter Items By</h6>
                                    {filters.some(filter => filter.column && filter.value) && (
                                        <span className={styles.secondaryBtnText} onClick={handleClearAllFilters}>
                                            Clear All
                                        </span>
                                    )}
                                </div>

                                {filters.map(filter => (
                                    <div key={filter.id} className="d-flex align-items-center gap-2 mb-2">
                                        <select
                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                            value={filter.column}
                                            onChange={e => handleUpdateFilter(filter.id, 'column', e.target.value)}>
                                            <option value="">Select Column</option>
                                            {getColumnOptions().map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        <select
                                            className={'form-select form-select-sm ' + styles.filterSelect}
                                            value={filter.value}
                                            onChange={e => handleUpdateFilter(filter.id, 'value', e.target.value)}
                                            disabled={!filter.column}>
                                            <option value="">Filter</option>
                                            {getValueOptions(filter.column).map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>

                                        {filters.length > 1 && (
                                            <button className="btn btn-link p-0" onClick={() => handleRemoveFilter(filter.id)} style={{ color: '#6c757d' }}>
                                                <Delete16Regular style={{ width: '16px', height: '16px' }} />
                                            </button>
                                        )}
                                    </div>
                                ))}

                                <span className={styles.secondaryBtnText + ' ms-1'} onClick={handleAddFilter}>
                                    Add another filter
                                </span>
                            </div>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            </div>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                measuring={taskTableMeasuring}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}>
                <div ref={containerRef} className={styles.kanbanContainer}>
                    {TASK_KANBAN_STATUSES.map(status => {
                        const statusTasks = tasksByStatus[status.value] || [];

                        return (
                            <div key={status.value} className={styles.kanbanColumn}>
                                <DroppableStatusColumn status={status}>
                                    <div className={`${styles.kanbanStatus} ${styles.kanbanHeader}`} style={{ background: status.color }}>
                                        <span> {status.label}</span> <span style={{ fontWeight: 400 }}> {statusTasks.length}</span>
                                    </div>
                                    <div className={`${styles.kanbanContent} ${styles.hideScrollbar}`}>
                                        <SortableContext items={statusTasks.map(task => task.id.toString())} strategy={verticalListSortingStrategy}>
                                            {statusTasks.map(task => (
                                                <SortableTaskCard
                                                    key={task.id}
                                                    task={task}
                                                    projectUsersAll={projectUsersAll}
                                                    onTaskClick={onTaskClick}
                                                    onTaskEdit={onTaskEdit}
                                                    onTaskDelete={onTaskDelete}
                                                    onStarClick={handleStarClick}
                                                    onOpenSidebar={onOpenSidebar}
                                                    tasksWithSubTasks={tasksWithSubTasks}
                                                    previewTaskId={previewTaskId}
                                                />
                                            ))}
                                        </SortableContext>
                                    </div>
                                </DroppableStatusColumn>
                            </div>
                        );
                    })}
                </div>
                <DragOverlay>
                    {activeTask ? (
                        <div className={styles.kanbanCardWrapper} style={{ opacity: 0.8, transform: 'rotate(5deg)' }}>
                            <KanbanTaskCard
                                task={activeTask}
                                projectUsersAll={projectUsersAll}
                                onTaskClick={onTaskClick}
                                onTaskEdit={onTaskEdit}
                                onTaskDelete={onTaskDelete}
                                tasksWithSubTasks={tasksWithSubTasks}
                            />
                        </div>
                    ) : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
};

// Custom ProgressCircle component
const ProgressCircle = ({ value }) => {
    const size = 14; // fixed size 16px
    const strokeWidth = 1; // thickness of the progress ring

    // circle math
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (value / 100) * circumference;

    return (
        <svg width={size} height={size} style={{ display: 'block' }} role="progressbar" aria-valuenow={value} aria-valuemin="0" aria-valuemax="100">
            {/* background circle */}
            {/* <circle stroke="#def" fill="transparent" strokeWidth={strokeWidth} r={radius} cx={size / 2} cy={size / 2} /> */}
            {/* progress circle */}
            <circle
                stroke="var(--Neutral-Gray-6)"
                fill="transparent"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                r={radius}
                cx={size / 2}
                cy={size / 2}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 0.5s ease' }}
            />
        </svg>
    );
};

// DroppableStatusColumn component
const DroppableStatusColumn = ({ status, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: status.value,
    });

    return (
        <>
            {React.Children.map(children, (child, index) => {
                if (index === 1) {
                    // The content div (second child)
                    return React.cloneElement(child, {
                        ref: setNodeRef,
                        style: {
                            ...child.props.style,
                            backgroundColor: isOver ? 'rgba(0, 123, 255, 0.1)' : 'transparent',
                            transition: 'background-color 0.2s ease',
                        },
                    });
                }
                return child;
            })}
        </>
    );
};

const SortableTaskCard = React.memo(function SortableTaskCard({
    task,
    projectUsersAll,
    onTaskClick,
    onTaskEdit,
    onTaskDelete,
    onStarClick,
    onOpenSidebar,
    tasksWithSubTasks = new Set(),
    previewTaskId = null,
}) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id.toString() });

    const isPreviewLoading = previewTaskId != null && String(previewTaskId) === String(task.id);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : isPreviewLoading ? 0.65 : 1,
        pointerEvents: isPreviewLoading ? 'none' : undefined,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={styles.kanbanCardWrapper}>
            <KanbanTaskCard
                task={task}
                projectUsersAll={projectUsersAll}
                onTaskClick={onTaskClick}
                onTaskEdit={onTaskEdit}
                onTaskDelete={onTaskDelete}
                onStarClick={onStarClick}
                onOpenSidebar={onOpenSidebar}
                tasksWithSubTasks={tasksWithSubTasks}
            />
        </div>
    );
});

// KanbanTaskCard component
const KanbanTaskCard = ({ task, projectUsersAll, onTaskClick, onTaskEdit, onTaskDelete, onStarClick, onOpenSidebar, tasksWithSubTasks = new Set() }) => {
    const dispatch = useDispatch();
    const [isEditingTitle, setIsEditingTitle] = React.useState(false);
    const [editingTitle, setEditingTitle] = React.useState('');
    const titleTextareaRef = React.useRef(null);

    const adjustTitleHeight = () => {
        const el = titleTextareaRef.current;
        if (!el) return;

        // Reset height to recalculate
        el.style.height = 'auto';

        // Let height grow to fit full content (no truncation)
        el.style.height = el.scrollHeight + 'px';
    };

    React.useEffect(() => {
        if (!isEditingTitle) return;

        const el = titleTextareaRef.current;
        if (!el) return;

        adjustTitleHeight();

        // Move caret to the end of the text
        const length = el.value.length;
        el.setSelectionRange(length, length);
    }, [isEditingTitle, editingTitle]);

    const getUserInfo = userId => {
        if (!userId || !Array.isArray(projectUsersAll)) return null;
        const user = projectUsersAll.find(user => user?.value?.user_id === userId);
        return user?.value || null;
    };

    const assigneeName =
        task?.assigned_to_name ||
        (task?.assigned_to ? getUserInfo(task.assigned_to)?.full_name : null) ||
        task?.assigned_to_offline ||
        null;

    // Safety check for task
    if (!task) {
        return null;
    }

    // Check if task has sub-tasks (optimized - O(1) lookup)
    const hasSubTasks = () => {
        return tasksWithSubTasks.has(task.id);
    };

    // Get priority info
    const getPriorityInfo = priority => {
        const priorityMap = {
            HIGH: { bg: '#2B35AF', text: 'High' },
            MEDIUM: { bg: '#4361EE', text: 'Medium' },
            LOW: { bg: '#4895EF', text: 'Low' },
            '': { bg: '#C0C6D8', text: '' },
        };
        return priorityMap[priority] || priorityMap[''];
    };

    const priorityInfo = getPriorityInfo(task.priority);

    // Convert HTML to plain text
    const convertHtmlToText = htmlString => {
        if (!htmlString) return '';

        // Create a temporary div element
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;

        // Get the text content and clean it up
        let text = tempDiv.textContent || tempDiv.innerText || '';

        // Replace common HTML entities
        text = text.replace(/&nbsp;/g, ' ');
        text = text.replace(/&amp;/g, '&');
        text = text.replace(/&lt;/g, '<');
        text = text.replace(/&gt;/g, '>');
        text = text.replace(/&quot;/g, '"');
        text = text.replace(/&#39;/g, "'");

        // Clean up extra whitespace
        text = text.replace(/\s+/g, ' ').trim();

        return text;
    };

    const handleTitleClick = e => {
        e.stopPropagation();
        setEditingTitle(convertHtmlToText(task?.task) || '');
        setIsEditingTitle(true);
    };

    const handleTitleKeyDown = async e => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            e.preventDefault();
            await handleTitleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditingTitle(false);
            setEditingTitle('');
        }
    };

    const handleTitleSave = async () => {
        const originalText = convertHtmlToText(task?.task || '');

        // Only save if text has actually changed
        if (editingTitle.trim() !== originalText.trim()) {
            try {
                await dispatch(
                    PatchTask({
                        body: { task: editingTitle },
                        id: task.id,
                        suppressToast: true,
                    }),
                );
            } catch (error) {
                console.error('Error updating task title from Kanban:', error);
            }
        }

        setIsEditingTitle(false);
    };

    const handleTitleBlur = async () => {
        await handleTitleSave();
    };

    // Format deadline date
    const formatDeadline = (deadline) => {
        if (!deadline) return 'No deadline';

        const deadlineDate = new Date(deadline);

        const formatDate = date => {
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        };

        return formatDate(deadlineDate);
    };

    // Format last updated time
    const formatLastUpdated = dateString => {
        if (!dateString) return '2hrs ago';
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

        if (diffInHours < 1) return 'Just now';
        if (diffInHours === 1) return '1hr ago';
        if (diffInHours < 24) return `${diffInHours}hrs ago`;

        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays === 1) return '1day ago';
        return `${diffInDays}days ago`;
    };

    return (
        <div
            className={styles.kanbanTaskCard}
            onClick={e => {
                // Only trigger task click if it's not from an interactive element
                if (!e.target.closest(`.${styles.kanbanStarIcon}`) && !e.target.closest(`.${styles.kanbanCommentIcon}`)) {
                    onTaskClick && onTaskClick(task);
                }
            }}>
            {/* Top Section */}
            <div className="d-flex justify-content-between ">
                <div className="d-flex align-items-center">
                    <CustomAvatar name={assigneeName || ''} size={24} />
                    <div className={styles.kanbanTagBadge}>
                        {getVisibleTaskTags(task).join(', ') || 'Tag'}
                    </div>
                </div>
                <div>
                    <div className={styles.kanbanStarIcon} onClick={e => onStarClick && onStarClick(e, task)} style={{ cursor: 'pointer' }}>
                        {task.starred ? <Star16Filled color="var(--Brand-Secondary)" /> : <Star16Regular />}
                    </div>
                </div>
            </div>
            {isEditingTitle ? (
                <textarea
                    ref={titleTextareaRef}
                    className={styles.kanbanTaskTitle}
                    value={editingTitle}
                    rows={1}
                    onChange={e => {
                        e.stopPropagation();
                        setEditingTitle(e.target.value);
                    }}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={handleTitleKeyDown}
                    onBlur={handleTitleBlur}
                    autoFocus
                    style={{
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        overflow: 'hidden',
                        whiteSpace: 'pre-wrap',
                        textOverflow: 'unset',
                        padding: 0,
                        margin: 0,
                        background: 'transparent',
                        width: '100%',
                        marginBottom: '4px',
                        marginTop: '8px',
                    }}
                />
            ) : (
                <div className={styles.kanbanTaskTitle} onClick={handleTitleClick}>
                    {convertHtmlToText(task?.task) || 'Untitled Task'}
                </div>
            )}
            <div className="" style={{ marginBottom: '41px' }}>
                <div className={styles.kanbanWorkstream}>{task.workstream_repr || 'Workstream'}</div>
                {hasSubTasks() && (
                    <div className="d-flex align-items-center gap-1" style={{ fontSize: '12px' }}>
                        <ErrorCircle16Regular color="var(--RAG-Red)" />
                        <span>Contains sub-tasks</span>
                    </div>
                )}
            </div>

            {/* Middle Section */}
            <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-1">
                    {(() => {
                        const isCompleted = task.status === 'COMPLETED' || task.status === 'COMPLETE';
                        const deadline = task.deadline ? new Date(task.deadline) : null;
                        const today = new Date();
                        // Normalize dates to midnight for accurate date-only comparison
                        if (deadline) {
                            deadline.setHours(0, 0, 0, 0);
                        }
                        today.setHours(0, 0, 0, 0);

                        const formattedDeadline = deadline ? deadline.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '-- --';

                        return (
                            <>
                                <div className="d-flex align-items-center justify-content-center">
                                    {(() => {
                                        if (!deadline || task.status === 'CANCELLED') {
                                            return null;
                                        }

                                        // Only show icon if deadline has passed (today > deadline)
                                        const isOverdue = today > deadline;

                                        if (isOverdue && isCompleted) {
                                            return <Checkmark16Regular style={{ color: 'var(--RAG-Green)' }} />;
                                        }

                                        if (isOverdue && !isCompleted) {
                                            return <ErrorCircle16Regular style={{ color: 'var(--RAG-Red)' }} />;
                                        }

                                        return null;
                                    })()}
                                </div>
                                <div
                                    className={styles.kanbanDateRange}
                                    style={{
                                        textDecoration: isCompleted || task.status === 'CANCELLED' ? 'line-through' : 'none',
                                    }}>
                                    {formattedDeadline}
                                </div>
                            </>
                        );
                    })()}
                </div>
                {task.priority && (
                    <div className={styles.kanbanPriorityBadge} style={{ backgroundColor: priorityInfo.bg }}>
                        {priorityInfo.text}
                    </div>
                )}
            </div>

            {/* Bottom Section */}
            <div className={styles.kanbanCardBottom}>
                <div className="d-flex align-items-center gap-1">
                    <div className={styles.kanbanLastUpdated}>Last updated</div>
                    <CustomAvatar name={task.last_updated_by_name || 'Unknown'} size={24} />

                    <div className={styles.kanbanTimeAgo}>{formatLastUpdated(task.updated_at || task.created_at)}</div>
                </div>
                <div className={styles.kanbanCardRight}>
                    <div
                        className={styles.kanbanCommentIcon}
                        onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            onOpenSidebar && onOpenSidebar(task);
                        }}
                        style={{ cursor: 'pointer' }}>
                        {task?.comments_count > 0 ? <Comment16Filled color="var(--Brand-Secondary)" /> : <CommentAdd16Regular />}
                    </div>
                </div>
            </div>
        </div>
    );
};

// KanbanView component
const KanbanView = ({ tasks, onStatusChange, projectUsersAll, onTaskClick, onTaskEdit, onTaskDelete, onOpenSidebar, previewTaskId = null }) => {
    return (
        <div className="mt-4">
            <TaskKanbanBoard
                tasks={tasks}
                onStatusChange={onStatusChange}
                projectUsersAll={projectUsersAll}
                onTaskClick={onTaskClick}
                onTaskEdit={onTaskEdit}
                onTaskDelete={onTaskDelete}
                onOpenSidebar={onOpenSidebar}
                previewTaskId={previewTaskId}
            />
        </div>
    );
};

export default TaskKanbanBoard;
export { KanbanView, KanbanTaskCard, SortableTaskCard, ProgressCircle };
